import {
  useCurrentUser,
  useLogin,
  useLogout,
  usePasswordResetConfirm,
  usePasswordResetRequest,
  useResendEmailVerification,
  useSignup,
  useVerifyEmail,
  type AuthError,
  type AuthUser,
} from '@hooks/useAuth';
import { toProblem } from '@lib/api/base';
import { useToast } from '@ui/use-toast';
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Define the context type
interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (
    email: string,
    password: string
  ) => Promise<{ success: boolean; code?: string; email?: string }>;
  signup: (
    firstName: string,
    lastName: string,
    email: string,
    companyName: string,
    password: string
  ) => Promise<boolean>;
  logout: () => void;
  verifyEmail: (key: string) => Promise<boolean>;
  resendVerificationEmail: (email: string) => Promise<boolean>;
  requestPasswordReset: (email: string) => Promise<boolean>;
  resetPassword: (
    uid: string,
    key: string,
    password1: string,
    password2: string
  ) => Promise<boolean>;
  error: string | null;
  clearError: () => void;
}

// Create the context with a default value
const AuthContext = createContext<AuthContextType | null>(null);

// Custom hook for easy use of the context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Props for the AuthProvider component
interface AuthProviderProps {
  children: React.ReactNode;
}

// AuthProvider component
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const googleConnectedToastShown = useRef(false);

  // Use React Query hooks
  const { data: userData, isLoading: userLoading, error: _userError } = useCurrentUser();

  // One-shot acknowledgment when the backend auto-connected a Google
  // account to this user (the flag arrives at most once per link).
  useEffect(() => {
    if (userData?.googleConnectedToast && !googleConnectedToastShown.current) {
      googleConnectedToastShown.current = true;
      toast({
        title: 'Google connected',
        description:
          'Google is now linked to your ClearOffer account. You can use either sign-in method.',
      });
    }
  }, [userData, toast]);

  const { mutateAsync: loginMutation, isPending: isLoggingIn, error: loginError } = useLogin();

  const { mutateAsync: signupMutation, isPending: isSigningUp, error: signupError } = useSignup();

  const { mutate: logoutMutation, isPending: isLoggingOut } = useLogout();

  const {
    mutateAsync: verifyEmailMutation,
    isPending: isVerifyingEmail,
    error: verifyEmailError,
  } = useVerifyEmail();

  const {
    mutateAsync: resendVerificationEmailMutation,
    isPending: isResendingEmail,
    error: resendEmailError,
  } = useResendEmailVerification();

  const {
    mutateAsync: passwordResetRequestMutation,
    isPending: isRequestingPasswordReset,
    error: passwordResetRequestError,
  } = usePasswordResetRequest();

  const {
    mutateAsync: passwordResetConfirmMutation,
    isPending: isConfirmingPasswordReset,
    error: passwordResetConfirmError,
  } = usePasswordResetConfirm();

  // Combine loading states
  const isLoading =
    userLoading ||
    isLoggingIn ||
    isSigningUp ||
    isLoggingOut ||
    isVerifyingEmail ||
    isResendingEmail ||
    isRequestingPasswordReset ||
    isConfirmingPasswordReset;

  // Set error from relevant error sources
  useEffect(() => {
    // Only set errors from auth operations
    const currentError =
      loginError ||
      signupError ||
      verifyEmailError ||
      resendEmailError ||
      passwordResetRequestError ||
      passwordResetConfirmError;

    if (currentError) {
      // Don't set errors for special login cases that trigger redirects
      const isAuthError =
        currentError && typeof currentError === 'object' && 'code' in currentError;
      if (isAuthError) {
        const authError = currentError as AuthError;
        const normalizedCode = authError.code?.toUpperCase();
        if (normalizedCode === 'EMAIL_NOT_VERIFIED' || normalizedCode === 'NO_ACTIVE_COMPANY') {
          return;
        }
      }

      if (currentError instanceof Error) {
        setError(currentError.message);
      } else {
        const problem = toProblem(currentError);
        setError(problem.detail || problem.title || 'Authentication error');
      }
    } else {
      setError(null);
    }
  }, [
    loginError,
    signupError,
    verifyEmailError,
    resendEmailError,
    passwordResetRequestError,
    passwordResetConfirmError,
  ]);

  // Login function
  const login = async (
    email: string,
    password: string
  ): Promise<{ success: boolean; code?: string; email?: string }> => {
    try {
      await loginMutation({ email, password });
      setError(null);
      return { success: true };
    } catch (err) {
      const authError = err as AuthError;
      const normalizedCode = authError.code?.toUpperCase();

      // Special handling for no active company error
      if (normalizedCode === 'NO_ACTIVE_COMPANY') {
        return {
          success: false,
          code: 'no_active_company',
          email: authError.email || email,
        };
      }

      if (normalizedCode === 'EMAIL_NOT_VERIFIED') {
        return {
          success: false,
          code: 'email_not_verified',
          email: authError.email || email,
        };
      }

      const problem = toProblem(err);

      // Set error in context for display
      setError(problem.detail || problem.title || 'An error occurred during login');

      return {
        success: false,
        code: problem.code ? problem.code.toLowerCase() : 'unknown_error',
        email: authError.email || email,
      };
    }
  };

  // Signup function
  const signup = async (
    firstName: string,
    lastName: string,
    email: string,
    companyName: string,
    password: string
  ): Promise<boolean> => {
    try {
      await signupMutation({
        firstName,
        lastName,
        email,
        companyName,
        password,
      });
      setError(null);
      return true;
    } catch (err) {
      const problem = toProblem(err);
      setError(problem.detail || problem.title || 'An error occurred during signup');
      return false;
    }
  };

  // Logout function
  const logout = () => {
    logoutMutation();
  };

  // Verify email function
  const verifyEmail = async (key: string): Promise<boolean> => {
    try {
      await verifyEmailMutation({ key });
      setError(null);
      return true;
    } catch (err) {
      const problem = toProblem(err);
      setError(problem.detail || problem.title || 'An error occurred during verification');
      return false;
    }
  };

  // Resend verification email function
  const resendVerificationEmail = async (email: string): Promise<boolean> => {
    try {
      await resendVerificationEmailMutation({ email });
      setError(null);
      return true;
    } catch (err) {
      const problem = toProblem(err);
      setError(problem.detail || problem.title || 'An error occurred while resending verification');
      return false;
    }
  };

  // Request password reset function
  const requestPasswordReset = async (email: string): Promise<boolean> => {
    try {
      await passwordResetRequestMutation({ email });
      setError(null);
      return true;
    } catch (err) {
      const problem = toProblem(err);
      setError(
        problem.detail || problem.title || 'An error occurred while requesting password reset'
      );
      return false;
    }
  };

  // Reset password function
  const resetPassword = async (
    uid: string,
    key: string,
    password1: string,
    password2: string
  ): Promise<boolean> => {
    try {
      await passwordResetConfirmMutation({
        uid,
        key,
        password1,
        password2,
      });
      setError(null);
      return true;
    } catch (err) {
      const problem = toProblem(err);
      setError(problem.detail || problem.title || 'An error occurred while resetting password');
      return false;
    }
  };

  // Function to clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user: userData || null,
        isAuthenticated: !!userData,
        isLoading,
        login,
        signup,
        logout,
        verifyEmail,
        resendVerificationEmail,
        requestPasswordReset,
        resetPassword,
        error,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
